import { View, Text, Image, Pressable, ScrollView } from "react-native";
import ScButton from "../ui-kit/button";
import { useRouter } from "expo-router";
import ScInput from "../ui-kit/input";
import InviteMembersModal from "../action-modals/add-members/add-members-modal";
import InvitedMemberChips from "./group-invited-member-chips";
import { useGroupForm } from "@/src/hooks/features/groups/use-group-form";
import { useTranslation } from "react-i18next";
import { AddCircle, Edit, Trash } from "iconsax-react-nativejs";
import BackNavigationButton from "@/src/components/ui-kit/back-navigation";
import { useEffect, useState } from "react";
import ScToast from "../ui-kit/toast";
import { GroupEditProps } from "@/src/models/group.model";
import { useAuth } from "@/src/contexts/auth.context";

type GroupCreationFormProps = Readonly<{
  mode: "create" | "edit";
  initialValues?: GroupEditProps;
}>;

const GroupCreationForm = ({
  mode,
  initialValues = {
    name: "",
    ItemId: "",
    coverImageUrl: "",
    profileImageUrl: "",
    description: "",
    isPrivate: false,
  },
}: GroupCreationFormProps) => {
  const [toast, setToast] = useState<{
    text1: string;
    type: "success" | "error";
  } | null>(null);

  useEffect(() => {
    if (!toast || toast.type === "error") return;
    const timer = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(timer);
  }, [toast]);
  const { user } = useAuth();

  const groupForm = useGroupForm(mode, initialValues, user!, {
    onSuccess: () =>
      router.replace({
        pathname: "/groups",
        params: { success: mode === "create" ? "create_true" : "update_true" },
      }),
    onError: (message) =>
      setToast({ text1: t(message ?? "ERROR_SERVER"), type: "error" }),
  });

  const router = useRouter();
  const { t } = useTranslation();

  return (
    <View className="flex-1 gap-4 p-0 md:p-6 rounded-none md:rounded-xl bg-obsidian md:bg-chalk border-0 md:border md:border-ash-3">
      {toast && (
        <View className="absolute top-0 left-0 right-0 z-50 items-center">
          <ScToast
            text1={toast.text1}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </View>
      )}
      <View className="flex-row items-center relative rounded-xl p-4 bg-chalk border border-ash-3">
        <BackNavigationButton
          iconSize={20}
          iconClassName="text-slate"
          labelClassName="text-slate"
          onPress={() => router.back()}
          containerStyle={{ marginBottom: 0 }}
        />
        <Text className="text-lg font-medium text-onyx absolute left-1/2 -translate-x-1/2">
          {mode === "create" ? t("LABEL_CREATE_GROUP") : t("LABEL_EDIT_GROUP")}
        </Text>
      </View>
      <View className="flex-1 flex-col p-5 rounded-xl border border-gray-light-3/20">
        <ScrollView showsVerticalScrollIndicator={true}>
          <View className="p-1 gap-6">
            <ScInput
              label="LABEL_NAME"
              placeholder="LABEL_PROMPT_GROUP_NAME"
              value={groupForm.name}
              onChangeText={groupForm.onChangeName}
            />

            <ScInput
              label="LABEL_DESCRIPTION"
              placeholder="LABEL_PROMPT_GROUP_DESCRIPTION"
              value={groupForm.desc}
              onChangeText={groupForm.onChangeDesc}
            />

            {/* TO-DO: Replace with component */}
            <View className="gap-2">
              <Text className="text-onyx">{t("LABEL_GROUP_VISIBILITY")}</Text>
              <View className="flex-row gap-4">
                <Pressable
                  className="flex-row items-center gap-2 px-4 py-3 hover:bg-ash-3 hover:rounded-lg"
                  onPress={() => groupForm.setIsPrivate(false)}
                >
                  <View
                    className={`w-5 h-5 rounded-full border-2 items-center justify-center ${!groupForm.isPrivate ? "border-primary" : "border-ash-3"}`}
                  >
                    {!groupForm.isPrivate && (
                      <View className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </View>
                  <Text className="text-onyx">{t("LABEL_PUBLIC_GROUP")}</Text>
                </Pressable>

                <Pressable
                  className="flex-row items-center gap-2 px-4 py-3 hover:bg-ash-3 hover:rounded-lg"
                  onPress={() => groupForm.setIsPrivate(true)}
                >
                  <View
                    className={`w-5 h-5 rounded-full border-2 items-center justify-center ${groupForm.isPrivate ? "border-primary" : "border-ash-3"}`}
                  >
                    {groupForm.isPrivate && (
                      <View className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </View>
                  <Text className="text-onyx">{t("LABEL_PRIVATE_GROUP")}</Text>
                </Pressable>
              </View>
            </View>

            {/* TO-DO: Replace with component */}
            <View className="gap-2">
              <Text className="text-onyx">
                {t("LABEL_GROUP_PROFILE_PHOTO")}
              </Text>
              <View className="flex-row items-center gap-4">
                <Pressable
                  onPress={groupForm.pickProfileImageAsync}
                  className={`w-24 h-24 rounded-full overflow-hidden items-center justify-center bg-ash hover:bg-ash-3 ${
                    groupForm.selectedProfileImage
                      ? ""
                      : "border-2 border-dashed border-ash-3"
                  }`}
                >
                  {groupForm.selectedProfileImage ? (
                    <>
                      <Image
                        className="w-full h-full"
                        resizeMode="cover"
                        source={{ uri: groupForm.selectedProfileImage.uri }}
                      />
                      <View className="absolute inset-0 bg-black/25 items-center justify-center">
                        <Edit color="white" size={20} />
                      </View>
                    </>
                  ) : (
                    <AddCircle className="text-slate" size={28} />
                  )}
                </Pressable>

                <View className="flex-1 gap-2">
                  <Text className="text-xs text-slate">
                    {t("HINT_GROUP_PROFILE_PHOTO")}
                  </Text>
                  {groupForm.selectedProfileImage && (
                    <Pressable
                      className="flex-row items-center gap-1.5 self-start px-2 py-1 rounded-lg hover:bg-ash-3"
                      onPress={() =>
                        groupForm.setSelectedProfileImage(undefined)
                      }
                    >
                      <Trash className="text-slate" size={16} />
                      <Text className="text-xs text-slate">
                        {t("BTN_REMOVE")}
                      </Text>
                    </Pressable>
                  )}
                </View>
              </View>
            </View>

            {/* TO-DO: Replace with component */}
            <View className="gap-2">
              <Text className="text-onyx">{t("LABEL_GROUP_COVER_PHOTO")}</Text>

              <Pressable
                onPress={groupForm.pickImageAsync}
                className={`w-full h-48 rounded-xl overflow-hidden border-2 border-dashed border-ash-3 items-center justify-center bg-ash hover:bg-ash-3 ${groupForm.selectedImage ? "border-none" : ""}`}
              >
                {groupForm.selectedImage ? (
                  <>
                    <Image
                      className="w-full h-full"
                      resizeMode="cover"
                      source={{ uri: groupForm.selectedImage.uri }}
                    />
                    <View className="absolute inset-0 bg-black/20 items-center justify-center">
                      <View className="flex-row gap-4">
                        <Pressable
                          onPress={groupForm.pickImageAsync}
                          className="bg-white/20 p-2 rounded-full hover:bg-ash-3"
                        >
                          <Edit color="white" />
                        </Pressable>
                        <Pressable
                          onPress={(e) => {
                            e.stopPropagation();
                            groupForm.setSelectedImage(undefined);
                          }}
                          className="bg-white/20 p-2 rounded-full hover:bg-ash-3"
                        >
                          <Trash color="white" />
                        </Pressable>
                      </View>
                    </View>
                  </>
                ) : (
                  <AddCircle className="text-slate" size={48} />
                )}
              </Pressable>
            </View>

            {mode === "create" && (
              <View className="gap-2">
                <Text className="text-onyx">{t("LABEL_INVITE_MEMBERS")}</Text>
                <ScButton
                  text="BTN_INVITE_MEMBERS"
                  onPress={() => groupForm.setMembersModalVisible(true)}
                  mode="outlined"
                  icon={<AddCircle />}
                  style={{ width: 180 }}
                />
                {groupForm.invitedMembers.length > 0 && (
                  <InvitedMemberChips
                    members={groupForm.invitedMembers}
                    onRemove={groupForm.removeInvited}
                  />
                )}
              </View>
            )}
          </View>

          <InviteMembersModal
            members={groupForm.invitedMembers}
            visible={groupForm.membersModalVisible}
            onClose={() => groupForm.setMembersModalVisible(false)}
            onInvite={(members) => groupForm.handleInvite(members)}
          />
        </ScrollView>

        <View className="flex-row justify-end gap-3">
          <ScButton
            text="BTN_CANCEL"
            mode="outlined"
            disabled={groupForm.isPending}
            onPress={() => {
              router.back();
            }}
          />
          <ScButton
            text="BTN_SUBMIT"
            onPress={groupForm.handleSubmit}
            disabled={groupForm.isPending}
            loading={groupForm.isPending}
          />
        </View>
      </View>
    </View>
  );
};

export default GroupCreationForm;
